'use client';

import { FC, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Globe, Plus, X, Menu, ChevronDown, User } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRouter, usePathname } from 'next/navigation';
import { useAppDispatch, useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { setDefaultCurrency } from '@/hotdesk-ui/store/slices/currency.slice';
import { fetchRemoteConfig } from '@/hotdesk-ui/common-classes/firebase.service';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';
import { HEADER_HEIGHT, HERO_SEARCH_SELECTOR } from '@/hotdesk-ui/constants/layout.constant';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import styles from './Navbar.module.scss';
import HeaderSearch from './HeaderSearch';
import WorkspaceTypeSwitcher from './WorkspaceTypeSwitcher';
import SearchModal from './SearchModal';
import CurrencySelect from './CurrencySelect';
import LanguageSelect from './LanguageSelect';
import CompanySetupCountryPopup from '@/hotdesk-ui/components/company-setup/CompanySetupCountryPopup';
import { buildLongTermOfficesExploreHref } from '@/hotdesk-ui/helpers/explore-location-params';
import { workspacesPageKind } from '@/hotdesk-ui/helpers/workspace-url';
export interface NavbarProps {
  isDirectory: boolean;
  languageCode: string;
  scrollToSection: (sectionId: string, eventName: string) => void;
  moveToRoute: (eventName?: string) => void;
  handleChange: (value: string) => void;
  osClick: () => void;
  style: string;
  signIn: () => void;
  signOut: () => void;
  login: boolean;
  user?: any;
  availableLanguages: {
    label: string;
    value: string;
  }[];
  pathname: string;
  /** When true, show Plans in the nav and navigate to `/subscriptions` (from `SubscriptionAvailability`). */
  subscriptionLink?: boolean;
  autocompleteInput: MutableRefObject<any>;
  autocompleteContainer: MutableRefObject<any>;
  getPermission: () => void;
  sentData: () => void;
  listingType: number;
  addFilters: (value: number) => void;
  showAuthSkeleton?: boolean;
  /** A place picked in the header search — the caller opens explore with it. */
  onSearchSelect: (result: GeoCodeResult) => void;
}

/**
 * Pages that render their own hero search (marked with `HERO_SEARCH_MARKER`).
 * They start out showing the type switcher so the header doesn't duplicate the
 * bar sitting right below it; the observer below then takes over. Seeding from
 * the path — rather than defaulting either way — keeps other pages from
 * flashing the switcher, and keeps these from flashing the header search while
 * their loading skeleton is up. Add a route here if it gains a hero search.
 */
const HERO_SEARCH_ROUTES = ['/', '/home'];

/** Matches `$md-width` — where the bar drops its centre track. */
const MOBILE_QUERY = '(max-width: 991px)';

/**
 * Routes with their own mobile header design: a collapsed search pill that
 * opens the search modal, with the type filters beneath it, stacked under the
 * brand row. These routes deliberately do NOT get the compact explore-style
 * bar in the brand row — this band is their search.
 *
 * `/home` is deliberately NOT here yet: its hero is a full-bleed dark image
 * with `margin-top: -140px` that tucks under the header, so a band stacked on
 * top of it needs a design decision before it can be turned on.
 */
const MOBILE_SEARCH_BAND_ROUTES = ['/'];

/**
 * Pages that open with their own search bar on mobile, directly under the
 * header: the country and city landing pages (not a state page). The brand
 * row skips its compact search there rather than stack two.
 */
const hasOwnMobileSearch = (pathname: string) => {
  const kind = workspacesPageKind(pathname);
  return kind === 'country' || kind === 'city';
};
const Navbar: FC<NavbarProps> = ({
  isDirectory,
  languageCode,
  scrollToSection,
  moveToRoute,
  handleChange,
  osClick,
  style,
  signIn,
  signOut,
  login,
  user,
  availableLanguages,
  subscriptionLink,
  autocompleteInput,
  autocompleteContainer,
  getPermission,
  sentData,
  listingType,
  addFilters,
  showAuthSkeleton = false,
  onSearchSelect
}) => {
  const t = useTranslations();
  const router = useRouter();
  const navPathname = usePathname();
  const dispatch = useAppDispatch();

  // On explore, the header centre hosts the page's own search bar (rendered
  // by ExploreToolbar and positioned into this slot), so the type switcher
  // is hidden here — an empty placeholder keeps the 3-column grid balanced.
  const isExplore = navPathname === '/workspaces';

  // Everywhere else the centre slot holds the search bar too. The landing
  // and /home are the exceptions *while* their own hero search is on screen:
  // there the switcher stays, and the header only takes the search over once
  // that hero bar has scrolled up under the header.
  const [heroSearchOnScreen, setHeroSearchOnScreen] = useState(() => HERO_SEARCH_ROUTES.includes(navPathname));
  useEffect(() => {
    if (isExplore) return;
    let intersection: IntersectionObserver | null = null;
    let pending: MutationObserver | null = null;
    const watch = (heroSearch: Element) => {
      // Shrink the root by the header's height, or a bar sitting directly
      // beneath it would still count as visible once it slid underneath.
      intersection = new IntersectionObserver(([entry]) => setHeroSearchOnScreen(entry.isIntersecting), {
        rootMargin: `-${HEADER_HEIGHT}px 0px 0px 0px`,
        threshold: 0
      });
      intersection.observe(heroSearch);
    };
    const heroSearch = document.querySelector(HERO_SEARCH_SELECTOR);
    if (heroSearch) {
      watch(heroSearch);
    } else if (HERO_SEARCH_ROUTES.includes(navPathname)) {
      // The route has a hero search but it isn't mounted yet — the page
      // is still on its loading skeleton. Keep the switcher (the seeded
      // state) and pick the hero up when it lands, rather than flashing
      // the header search in and straight back out.
      pending = new MutationObserver(() => {
        const late = document.querySelector(HERO_SEARCH_SELECTOR);
        if (!late) return;
        pending?.disconnect();
        pending = null;
        watch(late);
      });
      pending.observe(document.body, {
        childList: true,
        subtree: true
      });
    } else {
      setHeroSearchOnScreen(false);
    }
    return () => {
      intersection?.disconnect();
      pending?.disconnect();
    };
  }, [isExplore, navPathname]);

  // Mobile drops the centre track: the search bar pins into the brand row
  // itself (the same placement explore uses) and the type filters move to
  // the band below. Starts false so server and first client render agree.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const showMobileSearchBand = isMobile && MOBILE_SEARCH_BAND_ROUTES.includes(navPathname);

  // Mobile shows the compact bar in the brand row on every page — except
  // explore, which pins its own into the same spot, and the routes whose
  // band below already carries a search. Desktop still waits for the page's
  // hero search to scroll away before taking the slot from the switcher.
  const showHeaderSearch = !isExplore && !showMobileSearchBand && !(isMobile && hasOwnMobileSearch(navPathname)) && (isMobile || !heroSearchOnScreen);

  // On mobile, explore pins its toolbar search and other pages pin
  // HeaderSearch into the brand row. Pull the globe out of the right
  // section and centre it between the bar and the hamburger so the fixed
  // pill (z-index 1001) doesn't cover it. Desktop keeps the globe in the
  // right cluster so it sits after "+ List Your Workspace", not to its left.
  const hasPinnedHeaderSearch = isMobile && (isExplore || showHeaderSearch);

  // The band lives inside the fixed header, so `main`'s top padding has to
  // grow by the header's real height. Measured rather than hard-coded: the
  // filter row's height depends on how many types the API returned.
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !showMobileSearchBand) return;
    const root = document.documentElement;
    const apply = () => root.style.setProperty('--fixed-header-height', `${el.offsetHeight}px`);
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--fixed-header-height');
    };
  }, [showMobileSearchBand]);
  const currencyList = useAppSelector((state: RootState) => state.currency.currency);
  const defaultCurrency = useAppSelector((state: RootState) => state.currency.defaultCurrency);
  const geoLocation = useAppSelector((state: RootState) => state.geoLocation.geoLocationZipData);
  const [scrolled, setScrolled] = useState(false);
  const [companySetupPopupOpen, setCompanySetupPopupOpen] = useState(false);
  const isProduction = process.env.NEXT_PUBLIC_SELF_URL === 'https://www.hotdesk.com/';
  // Remote config `company_setup_switch` controls the Company Setup entry
  // on production only; non-production envs ignore RC for QA.
  const [companySetupEnabled, setCompanySetupEnabled] = useState(!isProduction);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [businessMenuOpen, setBusinessMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [localeDropdownOpen, setLocaleDropdownOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  // Currency state lives in Redux and is hydrated client-side from localStorage,
  // so it's empty during SSR and populated on the client. Gate the currency
  // dropdowns on `mounted` to keep the server and first-client renders identical
  // and avoid the hydration mismatch that otherwise tears the whole tree down.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    if (!isProduction) {
      setCompanySetupEnabled(true);
      return;
    }
    fetchRemoteConfig(RemoteConfigKeys.COMPANY_SETUP_SWITCH, 'boolean').then(value => setCompanySetupEnabled(value !== false)).catch(() => setCompanySetupEnabled(true));
  }, [isProduction]);
  const localeDropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      // A control that removes itself as part of its own click is already
      // detached here, and `contains()` reports a detached node as
      // outside — which would close panels that should stay open.
      if (target && !document.contains(target)) return;
      if (localeDropdownRef.current && !localeDropdownRef.current.contains(target)) {
        setLocaleDropdownOpen(false);
      }

      // The hamburger button is excluded: it fires on `click`, after this
      // `mousedown`, so closing here would let its own toggle reopen the
      // menu and make the X appear dead.
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(target) && hamburgerRef.current && !hamburgerRef.current.contains(target)) {
        setMobileMenuOpen(false);
      }
    };
    // Capture phase: the carousels below the header bind their own
    // mousedown handlers for drag, and a bubble-phase listener can be cut
    // off before it ever reaches `document`. Capture runs first, so the
    // menu closes wherever the click lands.
    document.addEventListener('mousedown', handleClickOutside, true);
    // Touch devices never fire mousedown until after a tap completes (and
    // may not at all), so mirror it.
    document.addEventListener('touchstart', handleClickOutside as EventListener, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside as EventListener, true);
    };
  }, []);
  const handleExploreClick = useCallback(() => {
    moveToRoute('explore_clicked');
  }, [moveToRoute]);
  const handleSubscriptionsClick = useCallback(() => {
    if (!subscriptionLink) return;
    router.push('/memberships');
  }, [subscriptionLink, router]);
  const handleListWorkspace = useCallback(() => {
    osClick();
    window.open('https://host.hotdesk.com/', '_blank', 'noopener,noreferrer');
  }, [osClick]);
  const handleHybridRemoteTeams = useCallback(() => {
    window.open('https://one.hotdesk.com/', '_blank', 'noopener,noreferrer');
  }, []);
  const handleCompanySetup = useCallback(() => {
    setCompanySetupPopupOpen(true);
  }, []);
  const handleLongTermOffices = useCallback(() => {
    moveToRoute('long_term_offices_clicked');
    const lang = languageCode?.startsWith('language.') ? languageCode.slice('language.'.length) : languageCode;
    router.push(buildLongTermOfficesExploreHref(geoLocation, lang));
  }, [moveToRoute, languageCode, geoLocation, router]);
  const handleSavingsCalculator = useCallback(() => {
    moveToRoute('calculator_clicked');
    router.push('/calculator');
  }, [moveToRoute, router]);
  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);
  const handleCurrencyChange = useCallback((code: string) => {
    const next = currencyList.find(c => c.code === code);
    if (next) dispatch(setDefaultCurrency(next));
  }, [currencyList, dispatch]);

  // The languageCode prop arrives as "language.en" / "language.ar" because
  // getDisplayLanguageCode() returns the i18n key, but the select options use
  // the raw code (en, ar, …). Strip the prefix so the current language stays
  // selected in the dropdown.
  const selectedLanguageCode = languageCode?.startsWith('language.') ? languageCode.slice('language.'.length) : languageCode;

  // Workspace-type switcher. On `/` these re-query the carousels in place;
  // elsewhere they open explore. It sits in the bar's centre track on
  // desktop and in the mobile band below it, so it's built once here and
  // placed in whichever of the two is active.
  //
  // While it's opened out to the full set of types it needs more of the row
  // than the centre track can give it — the action cluster's min-content
  // width shoulders it off centre — so "+ List your workspace" stands down
  // for as long as the bar is open.
  const [typeSwitcherExpanded, setTypeSwitcherExpanded] = useState(false);
  const typeSwitcher = <WorkspaceTypeSwitcher onExpandedChange={setTypeSwitcherExpanded} />;
  return <div ref={wrapperRef} className={`${styles.wrapper} ${hasPinnedHeaderSearch ? styles.wrapperPinnedSearch : ''}`} data-sentry-component="Navbar" data-sentry-source-file="Navbar.tsx">
            <nav className={`${styles.navbar} ${scrolled ? styles.navbarScrolled : ''} ${mobileMenuOpen ? styles.navbarMenuOpen : ''}`} role="navigation" aria-label="Main navigation">
                <div className={styles.inner}>
                    <Link href="/" className={styles.logo} aria-label="Hotdesk home" data-sentry-element="Link" data-sentry-source-file="Navbar.tsx">
                        <Image src="https://hotdesk-assets.s3.eu-west-1.amazonaws.com/landing_pages/main_landing/hotdesk_new_logo.png" alt="Hotdesk" width={47} height={32} priority data-sentry-element="Image" data-sentry-source-file="Navbar.tsx" />
                    </Link>

                    {/* Centre slot. Explore positions its own search bar here,
                        so it gets an empty div to keep the grid's middle
                        column; the mobile band routes keep the row clear
                        because their search sits below it. Everywhere else:
                        the header search — always on mobile, and on desktop
                        once the page's own hero search (if any) has scrolled
                        away — otherwise the type switcher. */}
                    {isExplore ? <div aria-hidden="true" /> : showMobileSearchBand ? null : showHeaderSearch ? <HeaderSearch onDetectLocation={getPermission} onSearch={sentData} onSelect={onSearchSelect} /> : typeSwitcher}

                    <div className={styles.rightSection}>
                        <button type="button" className={`${styles.listButton} ${typeSwitcherExpanded ? styles.listButtonStoodDown : ''}`} onClick={handleListWorkspace}
          // Out of the tab order and off the a11y tree while
          // it's collapsed, so it can't be reached in a state
          // where it is invisible and unclickable.
          tabIndex={typeSwitcherExpanded ? -1 : undefined} aria-hidden={typeSwitcherExpanded || undefined}>
                            <Plus size={14} strokeWidth={2.5} data-sentry-element="Plus" data-sentry-source-file="Navbar.tsx" />
                            {t('nav.button')}
                        </button>

                        <div className={styles.localeWrapper} ref={localeDropdownRef}>
                            <button type="button" className={styles.globeButton} onClick={() => setLocaleDropdownOpen(prev => !prev)} aria-label={t('explore.header.localization')} aria-expanded={localeDropdownOpen} aria-haspopup="true">
                                <Globe size={18} strokeWidth={1.5} data-sentry-element="Globe" data-sentry-source-file="Navbar.tsx" />
                            </button>
                            <div className={`${styles.localeDropdown} ${localeDropdownOpen ? styles.localeDropdownOpen : ''}`} role="dialog" aria-label={t('explore.header.localization')}>
                                <div className={styles.localeHeader}>{t('explore.header.localization')}</div>

                                <div className={styles.localeBody}>
                                    <div className={styles.localeField}>
                                        <span className={styles.localeFieldLabel}>{t('explore.header.language')}</span>
                                        <LanguageSelect label={t('explore.header.language')} value={selectedLanguageCode} options={availableLanguages} onChange={handleChange} hideLabel triggerVariant="locale" data-sentry-element="LanguageSelect" data-sentry-source-file="Navbar.tsx" />
                                    </div>

                                    <div className={styles.localeField}>
                                        <span className={styles.localeFieldLabel}>{t('explore.header.currency')}</span>
                                        <CurrencySelect label={t('explore.header.currency')} searchPlaceholder={t('explore.header.currencySearch')} emptyLabel={t('explore.header.currencyNoResults')} value={mounted ? defaultCurrency?.code || '' : ''} currencies={currencyList} onChange={handleCurrencyChange} disabled={!mounted || currencyList.length === 0} hideLabel triggerVariant="locale" data-sentry-element="CurrencySelect" data-sentry-source-file="Navbar.tsx" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button ref={hamburgerRef} type="button" className={styles.hamburger} onClick={() => setMobileMenuOpen(prev => !prev)} aria-label="Toggle menu" aria-expanded={mobileMenuOpen}>
                            {mobileMenuOpen ? <X size={24} strokeWidth={2} /> : <Menu size={24} strokeWidth={2} />}
                        </button>
                    </div>
                </div>

                {/* Mobile band — the landing's own search bar and the type
                    filters, stacked under the brand row. It's part of the
                    fixed header, so both stay put as the page scrolls. The bar
                    is a live input: focusing it drops the suggestions panel
                    underneath, the same as every other search bar. */}
                {showMobileSearchBand && <div className={styles.mobileSearchBand}>
                        <HeaderSearch variant="band" onDetectLocation={getPermission} onSearch={sentData} onSelect={onSearchSelect} />
                        {typeSwitcher}
                    </div>}

                <div ref={mobileMenuRef} className={`${styles.mobileMenu} ${!mobileMenuOpen ? styles.mobileMenuHidden : ''}`}>
                    {isDirectory && <Link href="/workspaces" className={styles.menuItem} onClick={() => {
          handleExploreClick();
          closeMobileMenu();
        }}>
                            {t('explore.header.explore')}
                        </Link>}

                    {subscriptionLink && <button type="button" className={styles.menuItem} onClick={() => {
          handleSubscriptionsClick();
          closeMobileMenu();
        }}>
                            {t('nav.subscribtion')}
                        </button>}

                    {/* "For Businesses" collapses the three B2B destinations
                        behind one row, as in the design. */}
                    <button type="button" className={`${styles.menuItem} ${styles.menuItemExpandable}`} onClick={() => setBusinessMenuOpen(prev => !prev)} aria-expanded={businessMenuOpen}>
                        <span>{t('nav.Company')}</span>
                        <span className={`${styles.menuChevron} ${businessMenuOpen ? styles.menuChevronOpen : ''}`}>
                            <ChevronDown size={14} strokeWidth={2} data-sentry-element="ChevronDown" data-sentry-source-file="Navbar.tsx" />
                        </span>
                    </button>

                    {businessMenuOpen && <div className={styles.menuSubList}>
                            <button type="button" className={`${styles.menuItem} ${styles.menuSubItem}`} onClick={() => {
            handleHybridRemoteTeams();
            closeMobileMenu();
          }}>
                                {t('nav.hybridRemoteTeams')}
                            </button>
                            {companySetupEnabled && <button type="button" className={`${styles.menuItem} ${styles.menuSubItem}`} onClick={() => {
            handleCompanySetup();
            closeMobileMenu();
          }}>
                                    {t('nav.companySetup')}
                                </button>}
                            <button type="button" className={`${styles.menuItem} ${styles.menuSubItem}`} onClick={() => {
            handleLongTermOffices();
            closeMobileMenu();
          }}>
                                {t('nav.longTermOffices')}
                            </button>
                            <button type="button" className={`${styles.menuItem} ${styles.menuSubItem}`} onClick={() => {
            handleSavingsCalculator();
            closeMobileMenu();
          }}>
                                {t('nav.calculator')}
                            </button>
                        </div>}

                    {/* The "+ List Your Workspace" button in the bar is hidden on
                        small screens, so surface it here instead. */}
                    <button type="button" className={`${styles.menuItem} ${styles.menuItemMobileOnly}`} onClick={() => {
          handleListWorkspace();
          closeMobileMenu();
        }}>
                        {t('nav.button')}
                    </button>

                    <div className={styles.menuSeparator} />

                    {showAuthSkeleton ? <div className={styles.menuFooter}>
                            <div className={styles.mobileAuthSkeleton} role="status" aria-live="polite" aria-label={t('nav.loginOrSignUp')} />
                        </div> : !user ? <div className={styles.menuFooter}>
                            <button type="button" className={styles.menuPrimaryBtn} onClick={() => {
            signIn();
            closeMobileMenu();
          }}>
                                {t('nav.loginOrSignUp')}
                            </button>
                        </div> : <>
                            {/* Account row collapses My Account / My Bookings /
                                My favorites, mirroring the "For Businesses" row above. */}
                            <button type="button" className={styles.userIdentity} onClick={() => setAccountMenuOpen(prev => !prev)} aria-expanded={accountMenuOpen}>
                                {user.meta?.images?.profileImage ? <Image src={user.meta.images.profileImage} alt="" width={36} height={36} className={styles.userAvatar} /> : <span className={styles.userAvatarFallback}>
                                        <User size={18} strokeWidth={2} />
                                    </span>}
                                <span className={styles.userIdentityText}>
                                    <span className={styles.userIdentityName}>
                                        {user.firstName} {user.lastName}
                                    </span>
                                    {user.email && <span className={styles.userIdentityEmail}>
                                            {user.email}
                                        </span>}
                                </span>
                                <span className={`${styles.menuChevron} ${accountMenuOpen ? styles.menuChevronOpen : ''}`}>
                                    <ChevronDown size={14} strokeWidth={2} />
                                </span>
                            </button>

                            {accountMenuOpen && <div className={`${styles.menuSubList} ${styles.menuAccountSubList}`}>
                                    <Link href="/my-account" className={`${styles.menuItem} ${styles.menuSubItem}`} onClick={closeMobileMenu}>
                                        <Image src="/assets/icons/user_account.svg" alt="" width={18} height={18} className={styles.menuItemIcon} />
                                        {t('explore.header.myAccount')}
                                    </Link>
                                    <Link href="/my-bookings" className={`${styles.menuItem} ${styles.menuSubItem}`} onClick={closeMobileMenu}>
                                        <Image src="/assets/icons/booking_page.svg" alt="" width={18} height={18} className={styles.menuItemIcon} />
                                        {t('bookings.pageName')}
                                    </Link>
                                    <Link href="/favorites" className={`${styles.menuItem} ${styles.menuSubItem}`} onClick={closeMobileMenu}>
                                        <Image src="/assets/icons/favorites_page.svg" alt="" width={18} height={18} className={styles.menuItemIcon} />
                                        {t('favorites.pageName')}
                                    </Link>
                                </div>}

                            <div className={styles.menuFooter}>
                                <button type="button" className={`${styles.menuPrimaryBtn} ${styles.menuLogoutBtn}`} onClick={() => {
              signOut();
              closeMobileMenu();
            }}>
                                    <Image src="/assets/icons/LogOut.svg" alt="" width={18} height={18} />
                                    {t('explore.header.signOut')}
                                </button>
                            </div>
                        </>}
                </div>
            </nav>

            <SearchModal isOpen={searchModalOpen} onClose={() => setSearchModalOpen(false)} autocompleteInput={autocompleteInput} autocompleteContainer={autocompleteContainer} getPermission={getPermission} sentData={sentData} listingType={listingType} addFilters={addFilters} data-sentry-element="SearchModal" data-sentry-source-file="Navbar.tsx" />

            <CompanySetupCountryPopup show={companySetupPopupOpen} onClose={() => setCompanySetupPopupOpen(false)} data-sentry-element="CompanySetupCountryPopup" data-sentry-source-file="Navbar.tsx" />
        </div>;
};
export default Navbar;